// src/prototype/components/EASurveyOverviewModal.tsx
// ============================================================
// EA CONTEXT MODULES MODAL (Refactored from "Data Intake Surveys")
// Modal wrapper that shows survey overview with EA journey framing
// and opens UnifiedSurveyModal when a module is selected.
// 
// FIXES:
// - toAnswered now handles both {response} and {answer} formats
// - Progress bar correctly shows completion state
// - UX refactored to "Context Modules" framing
// ============================================================

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, CheckCircle, Clock, AlertCircle, ChevronRight, Sparkles } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import SurveyService from '@/services/surveyService';
import UnifiedSurveyModal from '@/components/UnifiedSurveyModal';

interface EASurveyOverviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProgressUpdate?: (progress: { strategy: number; capabilities: number; readiness: number }) => void;
}

type SurveyType = 'strategy' | 'capabilities' | 'readiness';
type SurveyStatus = 'not-started' | 'in-progress' | 'complete';

interface SurveyProgress {
  answered: number;
  total: number;
  status: SurveyStatus;
}

export function EASurveyOverviewModal({ 
  isOpen, 
  onClose,
  onProgressUpdate 
}: EASurveyOverviewModalProps) {
  const { session, sessionLoading } = useAuth();
  const modalRef = useRef<HTMLDivElement>(null);
  
  // Survey state
  const [activeSurvey, setActiveSurvey] = useState<SurveyType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  
  // Progress tracking
  const [strategyProgress, setStrategyProgress] = useState<SurveyProgress>({ answered: 0, total: 9, status: 'not-started' });
  const [capabilitiesProgress, setCapabilitiesProgress] = useState<SurveyProgress>({ answered: 0, total: 14, status: 'not-started' });
  const [readinessProgress, setReadinessProgress] = useState<SurveyProgress>({ answered: 0, total: 14, status: 'not-started' });

  const clientId = session?.client_id || null;
  const userId = session?.user_id || 'admin';

  // ============================================================
  // FIX: Updated toAnswered to handle multiple response formats
  // API can return: [{question_id, response}] or [{answer}] or {qid: value}
  // ============================================================
  const toAnswered = (responses: any): number => {
    if (!responses) return 0;
    
    if (Array.isArray(responses)) {
      // Handle array of response objects
      return responses.filter((r: any) => {
        // Check all possible field names for the response value
        const value = r?.response ?? r?.answer ?? r?.value;
        return value !== undefined && value !== null && value !== '';
      }).length;
    }
    
    if (typeof responses === 'object') {
      // Handle object map: {question_id: response_value}
      return Object.values(responses).filter((v: any) => 
        v !== undefined && v !== null && v !== ''
      ).length;
    }
    
    return 0;
  };

  const getStatus = (completionStatus: string, answered: number, total: number): SurveyStatus => {
    // Check completion status first (takes precedence)
    if (completionStatus === 'complete') return 'complete';
    // Then check if any answers exist
    if (answered === 0) return 'not-started';
    return 'in-progress';
  };

  // ============================================================
  // FIX (P5): Derive the module counter from actual completion signals.
  // The API's raw `answered` field can read 0 even when a survey's sections
  // are complete, producing "0/9" next to a "Complete" badge. We reconcile
  // the count from status + percentage so the counter always matches the
  // section completion state.
  // ============================================================
  const deriveProgress = (raw: any, fallbackTotal: number): SurveyProgress => {
    const total = raw?.total || fallbackTotal;
    const apiStatus = raw?.status || 'not_started';
    const status: SurveyStatus = apiStatus === 'complete'
      ? 'complete'
      : apiStatus === 'in_progress'
        ? 'in-progress'
        : 'not-started';
    const pct = raw?.percentage ?? 0;
    const rawAnswered = raw?.answered ?? 0;
    const answered = status === 'complete'
      ? total
      : Math.max(rawAnswered, Math.round((pct / 100) * total));
    return { answered, total, status };
  };

  // Load survey progress
  const loadSurveyData = async () => {
    if (!clientId) return;

    try {
      setIsLoading(true);
      setLoadError(null);

      console.log('[EASurveyOverviewModal] Loading survey data for client:', clientId);
      const result = await SurveyService.getAllProgress(clientId, true);
      console.log('[EASurveyOverviewModal] Survey data result:', result);

      // ============================================================
      // FIX: API returns data directly with answered, total, status fields
      // NOT nested under responses/completionStatus
      // Format: {done, answered, total, status, percentage}
      // ============================================================
      
      if (result.strategy) {
        const progress = deriveProgress(result.strategy, 9);
        console.log('[EASurveyOverviewModal] Strategy progress:', progress);
        setStrategyProgress(progress);
      }

      if (result.capabilities) {
        const progress = deriveProgress(result.capabilities, 14);
        console.log('[EASurveyOverviewModal] Capabilities progress:', progress);
        setCapabilitiesProgress(progress);
      }

      if (result.readiness) {
        const progress = deriveProgress(result.readiness, 14);
        console.log('[EASurveyOverviewModal] Readiness progress:', progress);
        setReadinessProgress(progress);
      }

      // Notify parent of progress update
      if (onProgressUpdate) {
        onProgressUpdate({
          strategy: result.strategy?.percentage ?? 0,
          capabilities: result.capabilities?.percentage ?? 0,
          readiness: result.readiness?.percentage ?? 0,
        });
      }

    } catch (error) {
      // Static/no-backend demo fallback: show the three surveys as completed (coherent with
      // the client-side completion store — the surveys card is marked complete on entry) rather
      // than an error/empty state on Cloudflare.
      console.warn('[EASurveyOverviewModal] Survey fetch unavailable; using demo seed:', error);
      setStrategyProgress({ answered: 9, total: 9, status: 'complete' });
      setCapabilitiesProgress({ answered: 14, total: 14, status: 'complete' });
      setReadinessProgress({ answered: 14, total: 14, status: 'complete' });
      setLoadError(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && clientId) {
      loadSurveyData();
    }
  }, [isOpen, clientId]);

  // Focus trap and ESC handler
  useEffect(() => {
    if (!isOpen) return;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !activeSurvey) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, activeSurvey, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !activeSurvey) {
      onClose();
    }
  };

  const handleSurveyComplete = async () => {
    setActiveSurvey(null);
    await loadSurveyData(); // Refresh progress
  };

  const handleSurveySaveAndExit = async () => {
    setActiveSurvey(null);
    await loadSurveyData(); // Refresh progress
  };

  const getProgressPercent = (progress: SurveyProgress): number => {
    if (progress.total === 0) return 0;
    return Math.round((progress.answered / progress.total) * 100);
  };

  // ============================================================
  // REFACTOR: Module card with updated copy and styling
  // ============================================================
  const renderModuleCard = (
    title: string,
    description: string,
    surveyType: SurveyType,
    progress: SurveyProgress
  ) => {
    const getStatusBadge = () => {
      switch (progress.status) {
        case 'complete':
          return (
            <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 text-green-700">
              <CheckCircle className="w-3 h-3" />
              <span className="text-[11px] font-semibold">Complete</span>
            </div>
          );
        case 'in-progress':
          return (
            <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-700">
              <Clock className="w-3 h-3" />
              <span className="text-[11px] font-semibold">In Progress</span>
            </div>
          );
        default:
          return (
            <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-slate-600">
              <AlertCircle className="w-3 h-3" />
              <span className="text-[11px] font-semibold">Not Started</span>
            </div>
          );
      }
    };

    // REFACTOR: Updated CTA labels
    const getCtaLabel = () => {
      switch (progress.status) {
        case 'complete': return 'Edit Module';
        case 'in-progress': return 'Continue';
        default: return 'Begin';
      }
    };

    return (
      <div className="bg-white rounded-xl border border-[#e5e7eb] p-5 hover:shadow-md hover:border-[#18365E]/30 transition-all duration-200 flex flex-col h-full">
        {/* Header - fixed height area */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="text-base font-semibold text-[#18365E] leading-tight">{title}</h3>
          {getStatusBadge()}
        </div>

        {/* Description - flex-grow to fill available space */}
        <p className="text-sm text-[#717182] mb-4 leading-relaxed flex-grow">{description}</p>

        {/* Progress - pinned to bottom */}
        <div className="mb-4 mt-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-[#717182] font-medium">Progress</span>
            <span className={`text-xs font-semibold ${progress.status === 'complete' ? 'text-[#FF6E4C]' : 'text-[#18365E]'}`}>
              {progress.answered} / {progress.total} inputs
            </span>
          </div>
          {/* Progress bar - DVA brand orange */}
          <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ 
                width: `${getProgressPercent(progress)}%`,
                backgroundColor: '#FF6E4C'
              }}
            />
          </div>
        </div>

        {/* CTA - always at bottom */}
        <button
          onClick={() => setActiveSurvey(surveyType)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#18365E] text-white text-sm font-semibold hover:bg-[#18365E]/90 transition-colors"
        >
          {getCtaLabel()}
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    );
  };

  if (!isOpen) return null;

  // Get or create portal root
  let portalRoot = document.getElementById('ea-modal-root');
  if (!portalRoot) {
    portalRoot = document.createElement('div');
    portalRoot.id = 'ea-modal-root';
    document.body.appendChild(portalRoot);
  }

  return createPortal(
    <>
      {/* Main Overview Modal */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Context Modules"
        onClick={handleBackdropClick}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9998] p-4"
      >
        <div
          ref={modalRef}
          onClick={(e) => e.stopPropagation()}
          className="bg-[#F5F7FA] rounded-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden shadow-2xl flex flex-col"
        >
          {/* ============================================================
              REFACTOR A: Header with "Context Modules" framing
              ============================================================ */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#e5e7eb] bg-white">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold text-[#18365E]">Context Modules</h2>
                <Sparkles className="w-5 h-5 text-[#FF6E4C]" />
              </div>
              <p className="text-sm text-[#717182] mt-0.5">
                Each module adds signal to how opportunities are evaluated and ranked.
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Microcopy */}
              <span className="text-xs text-[#717182]/70 italic hidden sm:block">
                Changes apply immediately to results.
              </span>
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-lg bg-[#F5F7FA] hover:bg-[#e5e7eb] transition-colors flex items-center justify-center text-[#717182] hover:text-[#18365E]"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {sessionLoading || isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6E4C] mx-auto mb-3"></div>
                  <p className="text-sm text-[#717182]">Loading module progress...</p>
                </div>
              </div>
            ) : loadError ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
                  <p className="text-sm text-[#717182]">{loadError}</p>
                  <button
                    onClick={loadSurveyData}
                    className="mt-4 px-4 py-2 bg-[#18365E] text-white text-sm font-medium rounded-lg hover:bg-[#18365E]/90"
                  >
                    Retry
                  </button>
                </div>
              </div>
            ) : !clientId ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center max-w-md">
                  <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
                  <p className="text-sm text-[#717182]">
                    No organization selected. Please ensure you're logged in with an organization to access modules.
                  </p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
                {/* REFACTOR: Updated descriptions for EA context */}
                {renderModuleCard(
                  'Strategic Priorities',
                  'Define organizational strategy, innovation posture, and investment priorities to weight opportunities accordingly.',
                  'strategy',
                  strategyProgress
                )}
                {renderModuleCard(
                  'Data & Analytics Capabilities',
                  'Assess current data infrastructure and analytics maturity to calibrate feasibility scoring.',
                  'capabilities',
                  capabilitiesProgress
                )}
                {renderModuleCard(
                  'Data Readiness & Governance',
                  'Evaluate governance frameworks and organizational readiness to refine implementation estimates.',
                  'readiness',
                  readinessProgress
                )}
              </div>
            )}
          </div>

          {/* ============================================================
              REFACTOR: Footer with updated copy
              ============================================================ */}
          <div className="px-6 py-4 border-t border-[#e5e7eb] bg-white">
            <div className="flex items-center justify-between">
              <p className="text-xs text-[#717182]">
                These inputs immediately influence opportunity ranking and ROI assumptions.
              </p>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-[#717182] hover:text-[#18365E] transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Nested Survey Modal */}
      {activeSurvey && clientId && (
        <UnifiedSurveyModal
          isOpen={true}
          onClose={() => setActiveSurvey(null)}
          clientId={clientId}
          userId={userId}
          surveyType={activeSurvey}
          onComplete={handleSurveyComplete}
          onSaveAndExit={handleSurveySaveAndExit}
        />
      )}
    </>,
    portalRoot
  );
}

export default EASurveyOverviewModal;